import AbstractRestApi from '@/api/RestApi/AbstractRestApi'
import ResponseHandler from '@/api/response/ResponseHandler'
import apiClient from '@/api/ApiClient'
import Store from '@/store/store'
import MapperCollection from '@/services/mapper/MapperCollection'
import MapperType from '@/services/mapper/MapperType'
import configType from '@/store/type/deliveryType'

// tasks#64 (review R6-2): the per-order coalescing below (bf8899a3) already serializes
// concurrent callers for a changing order, but two reloads for the SAME order id (e.g. a
// stack_version event firing while the previous reload is still in flight) can still have
// their responses land out of order — this counter is the extra guard so only the response
// of the most recently STARTED request may ever commit.
let providerJobsRequestSeq = 0

class DeliveriesApiClient extends AbstractRestApi {
    getBaseUrl() {
        return '/api/v1/delivery-provider-jobs'
    }

    getDeliveryJobBaseUrl() {
        return '/api/v1/delivery-jobs'
    }

    clone(id) {
        return ResponseHandler.handle(
            apiClient.put(this.getItemIri(id, 'clone'))
        )
    }

    get(id) {
        return ResponseHandler.handle(apiClient.get(this.getItemIri(id)))
    }

    // The order page's provider rows. One global row array in the store (delivery.js), several
    // callers (the tab, the delivery block, the request modal, the Pusher mixin):
    // - one request in flight per client; a caller arriving meanwhile gets ONE follow-up request,
    //   started after the current one settles, for whichever order is current by then — it must
    //   see post-mutation data, the in-flight response may predate its change;
    // - a response (or a queued follow-up) for an order that is no longer current is dropped
    //   without committing, so a late answer for order A never overwrites order B's rows; the
    //   dropped caller resolves with {} (no @id, no hydra:member).
    getProviderJob(id) {
        // bump first: clearing the list must also invalidate a reload still in flight
        const seq = ++providerJobsRequestSeq
        this.providerJobIdentifier = id || null
        if (!id) {
            Store.commit(configType.mutations.SET_PROVIDER_JOBS, {})
            return Promise.resolve()
        }
        if (this.providerJobRequest) {
            if (!this.providerJobFollowUp) {
                this.providerJobFollowUp = this.providerJobRequest.then(() => {
                    this.providerJobFollowUp = null
                    const current = this.providerJobIdentifier
                    return current ? this.getProviderJob(current) : {}
                })
            }
            // a caller whose order is no longer current gets {} — also when the follow-up
            // (which ran for the current order) failed: another order's error is not its outcome
            return this.providerJobFollowUp.then(
                (result) => (this.providerJobIdentifier === id ? result : {}),
                (error) =>
                    this.providerJobIdentifier === id
                        ? Promise.reject(error)
                        : {}
            )
        }
        const request = ResponseHandler.handle(
            this.list({
                'deliveryJob.orders.identifier': id,
                itemsPerPage: 99,
            })
        ).then((body) => {
            if (
                this.providerJobIdentifier === id &&
                seq === providerJobsRequestSeq
            ) {
                Store.commit(
                    configType.mutations.SET_PROVIDER_JOBS,
                    body?.['hydra:member'] || body
                )
            }
            return body
        })
        // the tracker never rejects: the follow-up runs after a failed request too
        this.providerJobRequest = request
            .catch(() => {})
            .finally(() => {
                this.providerJobRequest = null
            })
        return request
    }

    getDeliveryJob(id) {
        if (!id) {
            Store.commit(configType.mutations.SET_DELIVERY_JOB, {})
            return Promise.resolve()
        }
        return ResponseHandler.handle(
            apiClient.get(`${this.getDeliveryJobBaseUrl()}/${id}`),
            configType.mutations.SET_DELIVERY_JOB,
            MapperCollection.get(MapperType.DELIVERY_FORM)
        )
    }

    // tasks#202: how the Onfleet estimated window was computed ({window, phase, lines}); super
    // admin only (a 404 for anybody else). Not routed through ResponseHandler on purpose: the
    // explanation is auxiliary, the caller hides it silently on any error.
    // Keep every call super-admin-gated: ApiClient turns a 404 for a restaurant admin into a
    // relogin + redirect, so an ungated call would throw them out of the order page.
    getEstimateExplanation(id) {
        return apiClient
            .get(`${this.getDeliveryJobBaseUrl()}/${id}/estimate-explanation`)
            .then((response) => response.data)
    }

    getTimeline(queryParams) {
        return ResponseHandler.handle(
            apiClient.get('/api/v1/delivery-job-log', {
                params: queryParams || {},
            })
        )
    }

    cancelDeliveryProviderJob(id) {
        return ResponseHandler.handle(
            apiClient.post(this.getItemIri(id, 'cancel'))
        )
    }

    sendDeliveryProviderJob(id) {
        return ResponseHandler.handle(
            apiClient.post(this.getItemIri(id, 'send'))
        )
    }

    setStuartDelay(deliveryJobId, enabled) {
        return ResponseHandler.handle(
            apiClient.post(
                `${this.getDeliveryJobBaseUrl()}/${deliveryJobId}/stuart-delay`,
                { enabled }
            )
        )
    }

    closeDeliveryProviderJob(id) {
        return ResponseHandler.handle(
            apiClient.post(this.getItemIri(id, 'close'))
        )
    }

    cancelDeliveryJob(id) {
        return ResponseHandler.handle(
            apiClient.post(`${this.getDeliveryJobBaseUrl()}/${id}/cancel`)
        )
    }

    // create delivery job
    create(entity) {
        return ResponseHandler.handle(
            apiClient.put(this.getDeliveryJobBaseUrl(), entity)
        )
    }

    updateDeliveryJob(entity) {
        return ResponseHandler.handle(
            apiClient.post(
                `${this.getDeliveryJobBaseUrl()}/${entity.id}`,
                entity
            ),
            configType.mutations.SET_DELIVERY_JOB,
            MapperCollection.get(MapperType.DELIVERY_FORM)
        )
    }

    sendOutDeliveryJob(id) {
        return ResponseHandler.handle(
            apiClient.post(`${this.getDeliveryJobBaseUrl()}/${id}/send-out`)
        )
    }

    createAndQueueDeliveryProviderJob(entity, id) {
        return ResponseHandler.handle(
            apiClient.put(
                `${this.getDeliveryJobBaseUrl()}/${id}/create-and-queue-provider-job`,
                entity
            )
        )
    }

    // Gophr price + distance per vehicle for the request-driver dialog (tasks#147); a failed vehicle
    // comes back as { vehicle, error } inside `quotes`, never as an HTTP error for the whole call.
    // params (tasks#194): optional { pickupTime, dropoffTime } as `YYYY-MM-DD HH:mm` server wall-clock;
    // the answer carries the resolved window as `pickupTime` / `dropoffTime` in the same format
    getGophrQuotes(deliveryJobId, params = {}) {
        return ResponseHandler.handle(
            apiClient.get(
                `${this.getDeliveryJobBaseUrl()}/${deliveryJobId}/gophr-quotes`,
                { params }
            )
        )
    }

    preCreateDeliveryCC(id, deliveryMethod) {
        return ResponseHandler.handle(
            apiClient.post(
                `${this.getDeliveryJobBaseUrl()}/pre-create-for/cloud-canteen/${id}`,
                {
                    deliveryMethod: deliveryMethod,
                }
            )
        )
    }

    preCreateDeliveryResidentialCC(id, deliveryMethod) {
        return ResponseHandler.handle(
            apiClient.post(
                `${this.getDeliveryJobBaseUrl()}/pre-create-for/residential-canteen/${id}`,
                {
                    deliveryMethod: deliveryMethod,
                }
            )
        )
    }

    forceFinishDeliveryJob(id) {
        return ResponseHandler.handle(
            apiClient.post(`${this.getDeliveryJobBaseUrl()}/${id}/force-finish`)
        )
    }

    getPedalme(lat, lon) {
        return ResponseHandler.handle(
            apiClient.get(`${this.getDeliveryJobBaseUrl()}/pedalme`, {
                params: {
                    lat,
                    lon,
                },
            })
        )
    }

    sendToPedalme(pickupTime, deliveryJobs, size) {
        return ResponseHandler.handle(
            apiClient.post(`${this.getDeliveryJobBaseUrl()}/send-to-pedalme`, {
                pickupTime,
                deliveryJobs,
                size,
            })
        )
    }

    removeDeliveryRuleOverrides(id) {
        return ResponseHandler.handle(
            apiClient.post(
                `${this.getDeliveryJobBaseUrl()}/${id}/remove-delivery-rule-overrides`,
                {}
            )
        )
    }

    setPriority(id, priority) {
        return ResponseHandler.handle(
            apiClient.post(`${this.getDeliveryJobBaseUrl()}/${id}/priority`, {
                priority,
            })
        )
    }
}

export default new DeliveriesApiClient()
